import { getItemPicAsset } from '../../utils/item';
import { evaluateMathExpression } from '../../utils/num';

const coerceInt = value => {
  const n = typeof value === 'string' ? parseInt(value, 10) : Number(value);
  return Number.isFinite(n) ? n : null;
};

export const resolveBound = (expr, contextItem, items) => {
  if (expr == null) return null;
  if (typeof expr === 'number') {
    return Number.isFinite(expr) ? expr : null;
  }
  if (typeof expr === 'string') {
    const trimmed = expr.trim();
    if (trimmed === '') return null;
    const evaluated = evaluateMathExpression(trimmed, contextItem, items);
    if (Number.isFinite(evaluated)) {
      return evaluated;
    }
    const asNumber = Number(trimmed);
    return Number.isFinite(asNumber) ? asNumber : null;
  }
  return null;
};

export const resolveIntBound = (expr, contextItem, items) => {
  const n = resolveBound(expr, contextItem, items);
  if (!Number.isFinite(n)) return null;
  return Math.round(n);
};

export const resolveFaceOverride = (buttonConfig, rolledValue) => {
  if (!buttonConfig || !Array.isArray(buttonConfig.faces)) {
    return null;
  }
  const v = coerceInt(rolledValue);
  if (!Number.isFinite(v)) {
    return null;
  }
  for (const face of buttonConfig.faces) {
    const fv = coerceInt(face?.value);
    if (fv === v) {
      return face;
    }
  }
  return null;
};

export const normalizeRules = rules => {
  if (!Array.isArray(rules)) return [];
  return rules
    .map(rule => {
      const safe = rule && typeof rule === 'object' ? rule : {};
      return {
        id: safe?.id || null,
        label: safe?.label || 'resultat',
        iconSlug: safe?.iconSlug ?? null,
        color: safe?.color ?? null,
        priority: Number.isFinite(Number(safe?.priority))
          ? Number(safe.priority)
          : 0,
        description:
          typeof safe?.description === 'string' ? safe.description : '',
        op: safe?.op || '>=',
        threshold:
          typeof safe?.threshold === 'string'
            ? safe.threshold
            : String(safe?.threshold ?? ''),
      };
    })
    .filter(r => r.op === 'default' || r.threshold.trim() !== '');
};

const resolveThreshold = (expr, sourceItem, items) => {
  if (expr == null) return null;
  if (typeof expr === 'number') return Number.isFinite(expr) ? expr : null;
  const s = String(expr).trim();
  if (!s) return null;
  const evaluated = evaluateMathExpression(s, sourceItem, items);
  if (Number.isFinite(evaluated)) return evaluated;
  const asNumber = Number(s);
  return Number.isFinite(asNumber) ? asNumber : null;
};

export const evaluateResultRule = (buttonConfig, value, sourceItem, items) => {
  const rulesRaw = normalizeRules(buttonConfig?.resultRules);
  if (!Number.isFinite(value) || rulesRaw.length === 0) {
    return null;
  }

  const rules = rulesRaw.map((rule, index) => ({
    ...rule,
    __index: index,
    __threshold:
      rule.op === 'default'
        ? null
        : resolveThreshold(rule.threshold, sourceItem, items),
  }));

  const comparePriority = (a, b) => {
    const pa = Number.isFinite(Number(a?.priority)) ? Number(a.priority) : 0;
    const pb = Number.isFinite(Number(b?.priority)) ? Number(b.priority) : 0;
    return pb - pa || a.__index - b.__index;
  };

  let defaultRule = null;
  const eq = [];
  const le = [];
  const ge = [];
  const ne = [];

  for (const rule of rules) {
    if (rule.op === 'default') {
      defaultRule = rule;
      continue;
    }

    const threshold = rule.__threshold;
    if (!Number.isFinite(threshold)) {
      continue;
    }

    const ok =
      rule.op === '>='
        ? value >= threshold
        : rule.op === '>'
          ? value > threshold
          : rule.op === '=='
            ? value === threshold
            : rule.op === '!='
              ? value !== threshold
              : rule.op === '<='
                ? value <= threshold
                : rule.op === '<'
                  ? value < threshold
                  : false;

    if (!ok) {
      continue;
    }

    if (rule.op === '==') eq.push(rule);
    else if (rule.op === '<' || rule.op === '<=') le.push(rule);
    else if (rule.op === '>' || rule.op === '>=') ge.push(rule);
    else if (rule.op === '!=') ne.push(rule);
  }

  const allMatches = [...eq, ...le, ...ge, ...ne];
  if (allMatches.length > 0) {
    // Priority wins first across all operators.
    const highestPriority = Math.max(
      ...allMatches.map(rule =>
        Number.isFinite(Number(rule?.priority)) ? Number(rule.priority) : 0,
      ),
    );
    const samePriority = allMatches.filter(rule => {
      const p = Number.isFinite(Number(rule?.priority))
        ? Number(rule.priority)
        : 0;
      return p === highestPriority;
    });

    const byPriorityEq = samePriority.filter(rule => rule.op === '==');
    const byPriorityLe = samePriority.filter(
      rule => rule.op === '<' || rule.op === '<=',
    );
    const byPriorityGe = samePriority.filter(
      rule => rule.op === '>' || rule.op === '>=',
    );
    const byPriorityNe = samePriority.filter(rule => rule.op === '!=');

    // Then prefer the most specific match within the same priority:
    // 1) equality
    // 2) <= / < : smallest threshold first (ex: <= X/2 before <= X)
    // 3) >= / > : largest threshold first (ex: >= 20 before >= 10)
    // 4) != (fallback)
    if (byPriorityEq.length > 0) {
      byPriorityEq.sort(comparePriority);
      return byPriorityEq[0];
    }

    if (byPriorityLe.length > 0) {
      byPriorityLe.sort((a, b) => {
        const prio = comparePriority(a, b);
        if (prio !== 0) {
          return prio;
        }
        const ta = a.__threshold;
        const tb = b.__threshold;
        if (!Number.isFinite(ta) || !Number.isFinite(tb)) {
          return a.__index - b.__index;
        }
        return ta - tb || a.__index - b.__index;
      });
      return byPriorityLe[0];
    }

    if (byPriorityGe.length > 0) {
      byPriorityGe.sort((a, b) => {
        const prio = comparePriority(a, b);
        if (prio !== 0) {
          return prio;
        }
        const ta = a.__threshold;
        const tb = b.__threshold;
        if (!Number.isFinite(ta) || !Number.isFinite(tb)) {
          return a.__index - b.__index;
        }
        return tb - ta || a.__index - b.__index;
      });
      return byPriorityGe[0];
    }

    if (byPriorityNe.length > 0) {
      byPriorityNe.sort(comparePriority);
      return byPriorityNe[0];
    }
  }

  return defaultRule;
};

export const evaluateResultLabel = (buttonConfig, value, sourceItem, items) => {
  const rule = evaluateResultRule(buttonConfig, value, sourceItem, items);
  return rule?.label || null;
};

export const getDiceEntryRenderState = ({
  entry,
  anim,
  now,
  newestKey,
  lastSeenRollKey,
  mountedAt,
  animationMs,
  buttons,
  items,
  contextItem,
  item,
}) => {
  const rollKey = entry?.rollId || entry?.at || null;
  const isAnimating = Boolean(anim);

  const hideResultUntilAnimStarts =
    lastSeenRollKey !== null &&
    newestKey &&
    rollKey === newestKey &&
    newestKey !== lastSeenRollKey;

  const withinAnimWindow =
    typeof entry?.at === 'number' &&
    entry.at > mountedAt &&
    now - entry.at < animationMs;

  const hideFinalResult =
    isAnimating || hideResultUntilAnimStarts || withinAnimWindow;

  const value = anim ? anim.displayValue : entry?.value;
  const buttonLabel =
    anim?.buttonLabel ||
    entry?.buttonLabel ||
    (Number.isFinite(entry?.sides) ? `d${entry.sides}` : null);
  const buttonId = anim?.buttonId || entry?.buttonId || null;
  const buttonConfig = buttonId
    ? buttons.find(b => b?.id === buttonId)
    : buttonLabel
      ? buttons.find(b => b?.label === buttonLabel)
      : null;

  const displayMode =
    buttonConfig?.multiDisplay === 'each' || entry?.multiDisplay === 'each'
      ? 'each'
      : 'total';

  const rolls = Array.isArray(entry?.rolls) ? entry.rolls : null;
  const animRolls = Array.isArray(anim?.displayRolls) ? anim.displayRolls : null;
  const face = resolveFaceOverride(buttonConfig, value);

  const fromItemId = anim ? anim.fromItemId : entry?.fromItemId;
  const fromItemLabel = anim ? anim.fromItemLabel : entry?.fromItemLabel;
  const normalizedRules = normalizeRules(buttonConfig?.resultRules);
  const ruleByLabel = !hideFinalResult
    ? normalizedRules.find(
        rule =>
          typeof entry?.resultLabel === 'string' &&
          typeof rule?.label === 'string' &&
          rule.label === entry.resultLabel,
      ) || null
    : null;

  const resultLabel = hideFinalResult
    ? null
    : entry?.resultLabel || null;
  const resultIconSlug = hideFinalResult
    ? null
    : ruleByLabel?.iconSlug || entry?.resultIconSlug || null;
  const resultColor = hideFinalResult
    ? null
    : ruleByLabel?.color || entry?.resultColor || null;
  const resultDescription = hideFinalResult
    ? null
    : (ruleByLabel?.description || '').trim() || null;

  const avatarBgColor = fromItemId
    ? getItemPicAsset(items?.[fromItemId], 0)?.bgColor || null
    : null;

  return {
    rollKey,
    hideFinalResult,
    value,
    buttonLabel,
    buttonConfig,
    displayMode,
    rolls,
    animRolls,
    face,
    fromItemId,
    fromItemLabel,
    avatarBgColor,
    resultLabel,
    resultIconSlug,
    resultColor,
    resultDescription,
  };
};
